import { AsyncPipe, NgTemplateOutlet } from "@angular/common";
import {
    ChangeDetectorRef,
    Component,
    inject,
    OnDestroy,
    OnInit,
} from "@angular/core";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { Observable, Subscription } from "rxjs";
import { filter, map } from "rxjs/operators";

import { Context } from "@hermes/core-app-core";
import {
    FooterComponent,
    FooterLightComponent,
} from "@hermes/domains-shell-fragments-footer";
import { FooterDeprecatedComponent } from "@hermes/domains-shell-fragments-footer-deprecated";
import {
    HeaderComponent,
    HeaderLightComponent,
} from "@hermes/domains-shell-fragments-header";
import { ShellFacade } from "@hermes/domains-shell-states-shell-state";
import { GeofilteringTrayService } from "@hermes/domains-shell-trays-geofiltering-tray";
import {
    FeatureFlagFacade,
    REDESIGN_FOOTER,
} from "@hermes/hermes-web-states-flipper-state";
import { TrayStackService } from "@hermes/hermes-web-states-tray-state";
import {
    HermesRoutes,
    SHELL_APPEARANCE_FULL,
    SHELL_APPEARANCE_LIGHT,
    SHELL_APPEARANCE_LIGHT_FOOTER_ONLY,
    SHELL_APPEARANCE_NONE,
    ShellAppearanceType,
} from "@hermes/utils-generic-constants";

import { NotificationModalComponent } from "../notification";
import { ParallaxLogoComponent } from "../parallax-logo/parallax-logo.component";

/**
 * Usage : add shell param on a route in app-routing.module.ts to change appearance
 * ```
 * { path: "/test-page", data: {shell: SHELL_APPEARANCE_LIGHT} }
 * ```
 *
 * Or change appearance by NGRX Shell State (see shell facade)
 */
export interface ShellRouteData extends HermesRoutes {
    /** style of backButton */
    backButton: "home";
}

/**
 * Shell component is the main wrapper of the site
 *
 * It is in charge of :
 *
 * Displaying header and footer independently of environment, it should take DrupalFeatures in consideration for angular first rollout.
 * Displaying light versions of header footer if we're on a "light" page ()
 * Making it so that every page has the same basic structure.
 */
@Component({
    selector: "h-shell",
    imports: [
        NotificationModalComponent,
        HeaderComponent,
        HeaderLightComponent,
        NgTemplateOutlet,
        FooterDeprecatedComponent,
        FooterComponent,
        AsyncPipe,
        FooterLightComponent,
        ParallaxLogoComponent,
    ],
    templateUrl: "./shell.component.html",
    styleUrls: ["./shell.component.scss"],
})
export class ShellComponent implements OnInit, OnDestroy {
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private changeDetector = inject(ChangeDetectorRef);
    private trayStack = inject(TrayStackService);
    private shellFacade = inject(ShellFacade);
    private geofilteringTrayService = inject(GeofilteringTrayService);
    private context = inject(Context);
    private featureFlagFacade = inject(FeatureFlagFacade);

    public isNewFooterActivateFF$: Observable<boolean> =
        this.featureFlagFacade.isActivated(REDESIGN_FOOTER);

    public trayOpened$ = this.trayStack.trays$.pipe(
        map((trays) => trays.length > 0),
    );

    /** On wechat payment pages  */
    public isBackButtonHome: boolean = false;

    public isFull$ = this.shellFacade.shell$.pipe(
        map((mode: ShellAppearanceType) => mode === SHELL_APPEARANCE_FULL),
    );

    public isLight$ = this.shellFacade.shell$.pipe(
        map((mode: ShellAppearanceType) =>
            [
                SHELL_APPEARANCE_LIGHT,
                SHELL_APPEARANCE_LIGHT_FOOTER_ONLY,
            ].includes(mode),
        ),
    );

    public noShell$ = this.shellFacade.shell$.pipe(
        map((mode: ShellAppearanceType) => mode === SHELL_APPEARANCE_NONE),
    );

    public footerOnly$ = this.shellFacade.shell$.pipe(
        map(
            (mode: ShellAppearanceType) =>
                mode === SHELL_APPEARANCE_LIGHT_FOOTER_ONLY,
        ),
    );

    private subscription = new Subscription();

    public ngOnInit(): void {
        if (this.context.isInBrowserMode()) {
            this.geofilteringTrayService.openGeofilteringTrayIfNeedeed();
        }

        this.subscription.add(
            this.router.events
                .pipe(
                    filter((event) => event instanceof NavigationEnd),
                    map(() => this.route),
                    // read the route tree and aggregate route data
                    map((route: ActivatedRoute) => {
                        let data: Partial<ShellRouteData> = {
                            ...route.snapshot.data,
                        };
                        while (route.firstChild) {
                            data = { ...data, ...route.snapshot.data };
                            route = route.firstChild;
                        }
                        return data;
                    }),
                )
                .subscribe((data: Partial<ShellRouteData>) => {
                    this.isBackButtonHome = data.backButton === "home";
                    this.changeDetector.markForCheck();
                }),
        );
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }
}
